import { addBankAccount } from 'api/CybridBank/addBankAccount';
import { getPlaidToken } from 'api/CybridBank/getPlaidToken';
import { initBankAddition } from 'api/CybridBank/initBankAdd';
import { AutoClose } from 'components/shared/AutoClose';
import LoadingScreen from 'components/shared/LoadingScreen';
import { usePolling } from 'hooks/usePolling';
import React, { useCallback, useEffect, useState } from 'react';
import { closeWindow } from "utils/closeWindow";

import BaseLayout from 'components/shared/BaseLayout';
import {
    PlaidEmbeddedLink,
    PlaidLinkError,
    PlaidLinkOnExit,
    PlaidLinkOnExitMetadata,
    PlaidLinkOnSuccess
} from 'react-plaid-link';
import { EventLevel, sendEvent } from 'utils/events/handler';

interface Props {
    token: string | null
    redirectUrl: string | null
}

const EVENT_BANK_ADD = "BANK_ADD";

const BANK_ADD_STATUS_FAILED = "FAILED";
const BANK_ADD_STATUS_COMPLETED = "COMPLETED";

const CybridBankAddition: React.FC<Props> = ({ token, redirectUrl }) => {
    const [polling, setPolling] = useState<boolean>(false);
    const [_token, setToken] = useState<string | null>(token);
    const [close, setClose] = useState<boolean>(false);

    useEffect(() => {
        if (token) return;

        const initializeToken = async () => {
            const success = await initBankAddition();
            if (success) {
                setPolling(true);
            }
        };
        initializeToken();
    }, [token]);

    usePolling({
        interval: 5_000,  // 5 seconds
        maxInterval: 120_000, // 2 minutes
        onPoll: async () => {
            const response = await getPlaidToken();
            return response;
        },
        onSuccess: (result) => {
            if (result.token) {
                setToken(result.token)
                setPolling(false);
            }
        },
        dependencies: [],
        dependencyCondition: !_token && polling,
    });

    const onSuccess: PlaidLinkOnSuccess = async (publicToken, metadata) => {
        await addBankAccount({
            partner_account_id: metadata.accounts[0].id,
            public_token: publicToken,
        });

        sendEvent(EVENT_BANK_ADD, EventLevel.INFO, { status: BANK_ADD_STATUS_COMPLETED });
        setClose(true);
    };

    const onExit: PlaidLinkOnExit = useCallback(async (error: PlaidLinkError | null, metadata: PlaidLinkOnExitMetadata) => {
        // TODO: Handle invalid link token
        // if (error != null && error.error_code === 'INVALID_LINK_TOKEN') {
        //     const success = await initBankAddition();
        //     if (success) {
        //         setPolling(true);
        //     }
        // }

        await addBankAccount({
            status: error?.error_code || "UNKNOWN_ERROR",
            error: error?.error_message || "Unknown error",
        });

        sendEvent(EVENT_BANK_ADD, EventLevel.ERROR, { status: BANK_ADD_STATUS_FAILED });
        setClose(true);
    },
        [],
    );

    const autoClose = () => {
        if (redirectUrl) {
            window.location.href = redirectUrl;
        } else {
            closeWindow()
        }
    }

    if (close) {
        return <AutoClose
            delayInSeconds={10}
            runner={autoClose}
            message="You can now close this window"
            headerVisible={false}
        />
    }

    const config = {
        token: _token,
        onSuccess,
        onExit,
    };

    if (!_token) {
        return <LoadingScreen headerVisible={false} />
    }

    return <BaseLayout
        body={<PlaidEmbeddedLink
            {...config}
            style={{
                height: '100%',
                width: '100%',
            }}
        />} footer={undefined}
        bottomSheet={undefined}
        headerVisible={false}
        brandingVisible={false}
    >
    </BaseLayout>;
};

export default CybridBankAddition;